// 📱 스마트폰 전용 마이그레이션 스크립트
// [배경] 스마트폰 데이터의 ID 불일치 문제 해결
// [실행] 브라우저 콘솔에서:
//   - window.__scanSmartphoneData(): 현재 스마트폰 데이터 스캔
//   - window.__diagnoseSmartphonePrices("Apple" | "Samsung"): 브랜드별 가격 매칭 진단
//   - window.__previewSmartphoneMigration(): 마이그레이션 미리보기
//   - window.__migrateSmartphones(): 실제 마이그레이션 실행
//
// ⚠️ 스마트폰 ID 문제 유형:
// 1. storage가 ID에 포함: smartphone-apple-iphone-16-iphone-16-pro-128gb-68
// 2. 순번이 ID에 포함: ...-68, ...-71
// 3. 연도가 모델명에 포함 (정상): galaxy-a3-2017 (이건 유지해야 함!)

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  generateModelId,
  generatePriceId,
  DeviceCatalogDocument,
  UnifiedPriceDocument
} from '../types/deviceCatalog';

// 🔑 스마트폰 ID 정규화 함수
// storage, 순번을 제거하고 순수 모델 ID만 추출
// ⚠️ 연도(2017, 2015 등)는 모델명의 일부이므로 유지!
const normalizeSmartphoneModelId = (modelId: string): string => {
  let normalized = modelId;
  
  // 1. 연도 패턴 보호 (임시 치환)
  // 예: galaxy-a3-2017, galaxy-j5-2015
  const yearPattern = /-(20\d{2})(?=-|$)/g;
  const years: string[] = [];
  normalized = normalized.replace(yearPattern, (match, year) => {
    years.push(year);
    return `-__YEAR${years.length - 1}__`;
  });
  
  // 2. 끝에 있는 2자리 이상 순번 제거
  // 예: -68, -71, -139
  normalized = normalized.replace(/-\d{2,}$/, '');
  
  // 3. storage 제거 (끝에 있는 경우)
  // 예: -128gb, -256gb, -512gb, -1tb
  normalized = normalized.replace(/-\d+(gb|tb)$/i, '');
  
  // 4. 중간에 있는 storage-순번 패턴 제거
  // 예: iphone-16-pro-128gb-68 → iphone-16-pro
  const storageMatch = normalized.match(/-(\d+)(gb|tb)-/i);
  if (storageMatch) {
    const idx = normalized.toLowerCase().indexOf(`-${storageMatch[1]}${storageMatch[2].toLowerCase()}-`);
    if (idx !== -1) {
      normalized = normalized.substring(0, idx);
    }
  }
  
  // 5. 연도 복원
  years.forEach((year, i) => {
    normalized = normalized.replace(`__YEAR${i}__`, year);
  });
  
  return normalized;
};

// 🔍 스마트폰 ID 상태 분석
const getSmartphoneIdStatus = (id: string, brand: string): {
  status: 'normal' | 'abnormal';
  issues: string[];
  suggestedId: string;
} => {
  const issues: string[] = [];
  let suggestedId = id;
  
  // 연도 패턴 보호 (검사에서 제외)
  const yearProtected = id.replace(/-(20\d{2})(?=-|$)/g, '-__YEAR__');
  
  // 문제 패턴 검사
  if (yearProtected.match(/-\d+(gb|tb)/i)) {
    issues.push('storage 포함');
  }
  if (yearProtected.match(/-\d{2,}$/) && !id.match(/-(20\d{2})$/)) {
    issues.push('순번 포함');
  }
  
  if (issues.length > 0) {
    suggestedId = normalizeSmartphoneModelId(id);
    return { status: 'abnormal', issues, suggestedId };
  }
  
  return { status: 'normal', issues: [], suggestedId: id };
};

/**
 * 🔍 스마트폰 데이터 스캔
 */
export async function scanSmartphoneData(): Promise<{
  catalog: { total: number; normal: number; abnormal: number; items: any[] };
  prices: { total: number; normal: number; abnormal: number; orphan: number; items: any[] };
}> {
  console.log('📱 스마트폰 데이터 스캔 시작...');
  console.log('============================================');
  
  const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
  const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
  
  // 스마트폰 모델 필터링
  console.log('\n📦 device_catalog 스마트폰 스캔...');
  const smartphoneModels: any[] = [];
  const smartphoneModelIds = new Set<string>();
  
  catalogSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    if (data.deviceType === 'smartphone') {
      const status = getSmartphoneIdStatus(docSnap.id, data.brand);
      smartphoneModels.push({
        id: docSnap.id,
        brand: data.brand,
        modelKo: data.modelKo,
        modelEn: data.modelEn,
        series: data.series,
        storageOptions: data.storageOptions,
        ...status
      });
      smartphoneModelIds.add(docSnap.id);
    }
  });
  
  // 스마트폰 가격 필터링
  console.log('💰 unified_prices 스마트폰 스캔...');
  const smartphonePrices: any[] = [];
  
  pricesSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    if (data.deviceType === 'smartphone') {
      const modelId = data.modelId || '';
      const isOrphan = modelId && !smartphoneModelIds.has(modelId);
      const status = getSmartphoneIdStatus(modelId, data.brand);
      
      smartphonePrices.push({
        id: docSnap.id,
        modelId,
        brand: data.brand,
        variant: data.variant,
        gradePrices: data.gradePrices,
        isOrphan,
        ...status
      });
    }
  });
  
  // 통계 계산
  const normalModels = smartphoneModels.filter(m => m.status === 'normal');
  const abnormalModels = smartphoneModels.filter(m => m.status === 'abnormal');
  const normalPrices = smartphonePrices.filter(p => p.status === 'normal' && !p.isOrphan);
  const abnormalPrices = smartphonePrices.filter(p => p.status === 'abnormal');
  const orphanPrices = smartphonePrices.filter(p => p.isOrphan);
  
  // 결과 출력
  console.log('\n============================================');
  console.log('📊 스마트폰 스캔 결과:');
  console.log(`📦 device_catalog: ${smartphoneModels.length}개`);
  console.log(`  ✅ 정상 ID: ${normalModels.length}개`);
  console.log(`  ⚠️ 비정상 ID: ${abnormalModels.length}개`);
  
  // 브랜드별 분류
  const byBrand: Record<string, { normal: number; abnormal: number }> = {};
  smartphoneModels.forEach(m => {
    if (!byBrand[m.brand]) byBrand[m.brand] = { normal: 0, abnormal: 0 };
    if (m.status === 'normal') byBrand[m.brand].normal++;
    else byBrand[m.brand].abnormal++;
  });
  
  console.log('\n  📊 브랜드별:');
  Object.entries(byBrand).forEach(([brand, stats]) => {
    console.log(`    ${brand}: 정상 ${stats.normal}개, 비정상 ${stats.abnormal}개`);
  });
  
  // 샘플 출력 (비정상 모델 최대 10개)
  if (abnormalModels.length > 0) {
    console.log(`\n  📋 비정상 ID 샘플 (${Math.min(10, abnormalModels.length)}/${abnormalModels.length}개):`);
    abnormalModels.slice(0, 10).forEach(m => {
      console.log(`    ⚠️ ${m.brand} - ${m.modelKo}`);
      console.log(`       현재: ${m.id}`);
      console.log(`       제안: ${m.suggestedId}`);
      console.log(`       문제: ${m.issues.join(', ')}`);
    });
  }
  
  console.log(`\n💰 unified_prices: ${smartphonePrices.length}개`);
  console.log(`  ✅ 정상 modelId: ${normalPrices.length}개`);
  console.log(`  ⚠️ 비정상 modelId: ${abnormalPrices.length}개`);
  console.log(`  🔗 고아 가격: ${orphanPrices.length}개`);
  
  // 고아 가격 샘플
  if (orphanPrices.length > 0) {
    console.log(`\n  📋 고아 가격 샘플 (${Math.min(5, orphanPrices.length)}/${orphanPrices.length}개):`);
    orphanPrices.slice(0, 5).forEach(p => {
      console.log(`    🔗 ${p.id}`);
      console.log(`       modelId: ${p.modelId} (device_catalog에 없음)`);
    });
  }
  
  console.log('\n============================================');
  
  return {
    catalog: {
      total: smartphoneModels.length,
      normal: normalModels.length,
      abnormal: abnormalModels.length,
      items: smartphoneModels
    },
    prices: {
      total: smartphonePrices.length,
      normal: normalPrices.length,
      abnormal: abnormalPrices.length,
      orphan: orphanPrices.length,
      items: smartphonePrices
    }
  };
}

/**
 * 🔍 브랜드별 스마트폰 가격 매칭 진단
 */
export async function diagnoseSmartphonePrices(brand: 'Apple' | 'Samsung'): Promise<void> {
  console.log(`🔍 ${brand} 스마트폰 가격 매칭 상세 진단`);
  console.log('============================================');
  
  const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
  const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
  
  // 해당 브랜드 스마트폰 모델 수집
  const models: any[] = [];
  catalogSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    if (data.deviceType === 'smartphone' && data.brand === brand) {
      models.push({
        id: docSnap.id,
        modelKo: data.modelKo,
        storageOptions: data.storageOptions || [],
        connectivityOptions: data.connectivityOptions || []
      });
    }
  });
  
  // 해당 브랜드 스마트폰 가격 수집
  const prices: any[] = [];
  pricesSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    if (data.deviceType === 'smartphone' && data.brand === brand) {
      prices.push({
        id: docSnap.id,
        modelId: data.modelId,
        variant: data.variant,
        gradePrices: data.gradePrices
      });
    }
  });
  
  console.log(`📦 ${brand} 스마트폰 모델: ${models.length}개`);
  console.log(`💰 ${brand} 스마트폰 가격: ${prices.length}개`);
  
  // 각 모델별 가격 매칭 확인
  console.log('\n🔗 모델-가격 매칭 확인:');
  
  let matchedModels = 0;
  let unmatchedModels = 0;
  let modelsWithPrice = 0;
  let modelsWithoutPrice = 0;
  
  models.forEach(model => {
    // 정확한 modelId 매칭
    const matchedPrices = prices.filter(p => p.modelId === model.id);
    
    if (matchedPrices.length > 0) {
      matchedModels++;
      
      // 가격이 설정된 것이 있는지 확인
      const hasActualPrice = matchedPrices.some(p => 
        p.gradePrices && (p.gradePrices.A > 0 || p.gradePrices.B > 0)
      );
      
      if (hasActualPrice) {
        modelsWithPrice++;
        console.log(`\n  ✅ ${model.modelKo}`);
        console.log(`     id: ${model.id}`);
        console.log(`     storage: ${model.storageOptions.join(', ')}`);
        console.log(`     매칭된 가격: ${matchedPrices.length}개`);
        matchedPrices.slice(0, 2).forEach(p => {
          console.log(`       - ${p.variant?.storage || '?'}: A등급 ${p.gradePrices?.A || 0}원`);
        });
        if (matchedPrices.length > 2) {
          console.log(`       ... 그 외 ${matchedPrices.length - 2}개 더 있음`);
        }
      } else {
        modelsWithoutPrice++;
        console.log(`\n  ⚠️ ${model.modelKo} (가격 미설정)`);
        console.log(`     id: ${model.id}`);
        console.log(`     매칭된 가격: ${matchedPrices.length}개 (모두 0원)`);
      }
    } else {
      unmatchedModels++;
      console.log(`\n  ❌ ${model.modelKo}`);
      console.log(`     id: ${model.id}`);
      console.log(`     매칭된 가격: 0개`);
    }
  });
  
  // 고아 가격 (모델에 매칭 안 되는 가격)
  const modelIds = new Set(models.map(m => m.id));
  const orphanPrices = prices.filter(p => !modelIds.has(p.modelId));
  
  console.log('\n============================================');
  console.log(`📊 ${brand} 스마트폰 매칭 요약:`);
  console.log(`  ✅ 가격 있는 모델: ${modelsWithPrice}개`);
  console.log(`  ⚠️ 가격 미설정 모델: ${modelsWithoutPrice}개`);
  console.log(`  ❌ 매칭 안 된 모델: ${unmatchedModels}개`);
  console.log(`  🔗 고아 가격: ${orphanPrices.length}개`);
  
  if (orphanPrices.length > 0) {
    console.log('\n🔗 고아 가격 목록 (device_catalog에 모델 없음):');
    orphanPrices.slice(0, 10).forEach(p => {
      console.log(`  - ${p.id}`);
      console.log(`    modelId: ${p.modelId}`);
    });
    if (orphanPrices.length > 10) {
      console.log(`  ... 그 외 ${orphanPrices.length - 10}개 더 있음`);
    }
  }
  
  console.log('\n============================================');
}

/**
 * 🔍 스마트폰 마이그레이션 미리보기
 */
export async function previewSmartphoneMigration(): Promise<void> {
  console.log('📱 스마트폰 마이그레이션 미리보기 (Dry Run)');
  console.log('============================================');
  console.log('⚠️ 이 작업은 데이터를 변경하지 않습니다.\n');
  
  const scanResult = await scanSmartphoneData();
  
  // 변환 계획 수립
  const catalogChanges: { oldId: string; newId: string; modelKo: string; brand: string }[] = [];
  const priceChanges: { priceId: string; oldModelId: string; newModelId: string }[] = [];
  
  // 1. device_catalog 변환 계획
  scanResult.catalog.items.forEach(model => {
    if (model.status === 'abnormal' && model.id !== model.suggestedId) {
      catalogChanges.push({
        oldId: model.id,
        newId: model.suggestedId,
        modelKo: model.modelKo,
        brand: model.brand
      });
    }
  });
  
  // 2. unified_prices modelId 변환 계획
  scanResult.prices.items.forEach(price => {
    if (price.status === 'abnormal' && price.modelId !== price.suggestedId) {
      priceChanges.push({
        priceId: price.id,
        oldModelId: price.modelId,
        newModelId: price.suggestedId
      });
    }
  });
  
  // 브랜드별 분류
  const appleChanges = catalogChanges.filter(c => c.brand === 'Apple');
  const samsungChanges = catalogChanges.filter(c => c.brand === 'Samsung');
  
  console.log('\n🔄 마이그레이션 예정 작업:');
  console.log(`\n📦 device_catalog ${catalogChanges.length}개 변환 예정:`);
  console.log(`  Apple: ${appleChanges.length}개`);
  console.log(`  Samsung: ${samsungChanges.length}개`);
  
  // Apple 샘플 출력
  if (appleChanges.length > 0) {
    console.log('\n  📱 Apple 샘플:');
    appleChanges.slice(0, 5).forEach(change => {
      console.log(`    ${change.modelKo}`);
      console.log(`      ${change.oldId}`);
      console.log(`      → ${change.newId}`);
    });
    if (appleChanges.length > 5) {
      console.log(`    ... 그 외 ${appleChanges.length - 5}개 더 있음`);
    }
  }
  
  // Samsung 샘플 출력
  if (samsungChanges.length > 0) {
    console.log('\n  📱 Samsung 샘플:');
    samsungChanges.slice(0, 5).forEach(change => {
      console.log(`    ${change.modelKo}`);
      console.log(`      ${change.oldId}`);
      console.log(`      → ${change.newId}`);
    });
    if (samsungChanges.length > 5) {
      console.log(`    ... 그 외 ${samsungChanges.length - 5}개 더 있음`);
    }
  }
  
  console.log(`\n💰 unified_prices ${priceChanges.length}개 modelId 수정 예정`);
  
  // 중복 ID 경고
  const newIdCounts = new Map<string, number>();
  catalogChanges.forEach(c => {
    newIdCounts.set(c.newId, (newIdCounts.get(c.newId) || 0) + 1);
  });
  
  const duplicates = Array.from(newIdCounts.entries()).filter(([_, count]) => count > 1);
  if (duplicates.length > 0) {
    console.log(`\n⚠️ 주의: ${duplicates.length}개 ID가 중복됩니다!`);
    console.log('   이 모델들은 병합이 필요합니다:');
    duplicates.slice(0, 5).forEach(([id, count]) => {
      console.log(`   - ${id}: ${count}개 모델이 같은 ID로 변환됨`);
      const models = catalogChanges.filter(c => c.newId === id);
      models.slice(0, 3).forEach(m => console.log(`     · ${m.modelKo} (${m.oldId})`));
    });
  } else {
    console.log('\n✅ 중복 ID 없음 - 안전하게 마이그레이션 가능');
  }
  
  console.log('\n============================================');
  console.log('📌 실제 마이그레이션을 실행하려면:');
  console.log('   await window.__migrateSmartphones()');
}

/**
 * 🔄 스마트폰 마이그레이션 실행
 */
export async function migrateSmartphones(): Promise<void> {
  console.log('📱 스마트폰 마이그레이션 시작...');
  console.log('============================================');
  console.log('⚠️ 이 작업은 데이터를 변경합니다!\n');
  
  const scanResult = await scanSmartphoneData();
  
  let catalogUpdated = 0;
  let catalogMerged = 0;
  let pricesUpdated = 0;
  const errors: string[] = [];
  
  // 1. 중복 ID 그룹 파악
  const idGroups = new Map<string, string[]>();
  scanResult.catalog.items.forEach(model => {
    if (model.status === 'abnormal') {
      const newId = model.suggestedId;
      if (!idGroups.has(newId)) {
        idGroups.set(newId, []);
      }
      idGroups.get(newId)!.push(model.id);
    }
  });
  
  console.log('🔧 1단계: device_catalog 정리...');
  
  for (const [newId, oldIds] of idGroups) {
    try {
      const existingDoc = await getDoc(doc(db, 'device_catalog', newId));
      
      if (existingDoc.exists()) {
        // 이미 존재하면 old ID들만 삭제
        for (const oldId of oldIds) {
          if (oldId !== newId) {
            await deleteDoc(doc(db, 'device_catalog', oldId));
            console.log(`  🗑️ 삭제 (중복): ${oldId}`);
            catalogMerged++;
          }
        }
      } else {
        // 첫 번째 모델을 canonical ID로 복사
        const firstOldId = oldIds[0];
        const firstDocSnap = await getDoc(doc(db, 'device_catalog', firstOldId));
        
        if (firstDocSnap.exists()) {
          const data = firstDocSnap.data();
          
          // 모든 storageOptions 병합
          const allStorageOptions = new Set<string>();
          
          for (const oldId of oldIds) {
            const oldDocSnap = await getDoc(doc(db, 'device_catalog', oldId));
            if (oldDocSnap.exists()) {
              const oldData = oldDocSnap.data();
              (oldData.storageOptions || []).forEach((s: string) => allStorageOptions.add(s));
            }
          }
          
          // 새 문서 생성
          await setDoc(doc(db, 'device_catalog', newId), {
            ...data,
            id: newId,
            storageOptions: Array.from(allStorageOptions),
            updatedAt: Timestamp.now(),
            updatedBy: 'smartphone-migration'
          });
          
          console.log(`  ✅ 생성: ${newId}`);
          console.log(`     storage: ${Array.from(allStorageOptions).join(', ')}`);
          catalogUpdated++;
          
          // 기존 문서들 삭제
          for (const oldId of oldIds) {
            if (oldId !== newId) {
              await deleteDoc(doc(db, 'device_catalog', oldId));
              console.log(`  🗑️ 삭제: ${oldId}`);
              catalogMerged++;
            }
          }
        }
      }
    } catch (error) {
      errors.push(`catalog ${newId}: ${(error as Error).message}`);
    }
  }
  
  // 2. unified_prices의 modelId 업데이트
  console.log('\n🔧 2단계: unified_prices modelId 업데이트...');
  
  const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
  
  for (const docSnap of pricesSnapshot.docs) {
    const data = docSnap.data();
    if (data.deviceType !== 'smartphone') continue;
    
    const modelId = data.modelId || '';
    const status = getSmartphoneIdStatus(modelId, data.brand);
    
    if (status.status === 'abnormal' && modelId !== status.suggestedId) {
      try {
        await setDoc(doc(db, 'unified_prices', docSnap.id), {
          ...data,
          modelId: status.suggestedId,
          updatedAt: Timestamp.now(),
          updatedBy: 'smartphone-migration'
        });
        
        console.log(`  ✅ ${docSnap.id.substring(0, 50)}...`);
        console.log(`     modelId: → ${status.suggestedId}`);
        pricesUpdated++;
      } catch (error) {
        errors.push(`price ${docSnap.id}: ${(error as Error).message}`);
      }
    }
  }
  
  // 결과 출력
  console.log('\n============================================');
  console.log('📊 마이그레이션 결과:');
  console.log(`  ✅ device_catalog 생성: ${catalogUpdated}개`);
  console.log(`  🗑️ device_catalog 병합/삭제: ${catalogMerged}개`);
  console.log(`  ✅ unified_prices modelId 수정: ${pricesUpdated}개`);
  
  if (errors.length > 0) {
    console.log(`\n❌ 에러: ${errors.length}개`);
    errors.slice(0, 10).forEach(e => console.log(`  - ${e}`));
  } else {
    console.log('\n🎉 스마트폰 마이그레이션 완료!');
    console.log('\n📌 다음 단계:');
    console.log('   await window.__scanSmartphoneData()  // 결과 확인');
  }
}

/**
 * 🔍 중복 병합 분석 (스마트폰)
 */
export async function analyzeSmartphoneMergeConflicts(): Promise<void> {
  console.log('🔍 스마트폰 중복 병합 상세 분석');
  console.log('============================================');
  
  const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
  
  const smartphoneModels: any[] = [];
  
  catalogSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    if (data.deviceType === 'smartphone') {
      const normalizedId = normalizeSmartphoneModelId(docSnap.id);
      smartphoneModels.push({
        id: docSnap.id,
        normalizedId,
        brand: data.brand,
        modelKo: data.modelKo,
        modelEn: data.modelEn,
        storageOptions: data.storageOptions
      });
    }
  });
  
  // 정규화된 ID별 그룹화
  const groups = new Map<string, any[]>();
  smartphoneModels.forEach(model => {
    if (!groups.has(model.normalizedId)) {
      groups.set(model.normalizedId, []);
    }
    groups.get(model.normalizedId)!.push(model);
  });
  
  // 2개 이상 모델이 같은 ID로 병합되는 경우만 필터
  const mergeGroups = Array.from(groups.entries())
    .filter(([_, models]) => models.length > 1);
  
  console.log(`📊 총 ${mergeGroups.length}개 그룹이 병합 예정\n`);
  
  // 각 그룹 분석
  const conflicts: { normalizedId: string; models: any[] }[] = [];
  const normalMerges: { normalizedId: string; count: number }[] = [];
  
  mergeGroups.forEach(([normalizedId, models]) => {
    const uniqueNames = new Set(models.map(m => m.modelKo));
    
    if (uniqueNames.size > 1) {
      conflicts.push({ normalizedId, models });
    } else {
      normalMerges.push({ normalizedId, count: models.length });
    }
  });
  
  console.log(`✅ 정상 병합 (같은 모델명): ${normalMerges.length}개 그룹`);
  normalMerges.slice(0, 5).forEach(m => {
    console.log(`   - ${m.normalizedId}: ${m.count}개 variant`);
  });
  if (normalMerges.length > 5) {
    console.log(`   ... 그 외 ${normalMerges.length - 5}개 더 있음`);
  }
  
  if (conflicts.length > 0) {
    console.log(`\n⚠️ 주의 필요 (다른 모델명): ${conflicts.length}개 그룹`);
    
    conflicts.forEach(({ normalizedId, models }) => {
      console.log(`\n🔴 ${normalizedId}`);
      models.forEach(m => {
        console.log(`   - "${m.modelKo}" (${m.brand})`);
        console.log(`     id: ${m.id}`);
      });
    });
  } else {
    console.log('\n✅ 모든 병합 그룹이 같은 모델명을 가지고 있습니다.');
  }
  
  console.log('\n============================================');
}

/**
 * 🔍 Apple 가격 데이터 심층 진단
 * - 누락된 Apple 가격을 찾습니다
 */
export async function diagnoseApplePricesDeep(): Promise<void> {
  console.log('🍎 Apple 스마트폰 가격 심층 진단');
  console.log('============================================');
  
  const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
  
  // 모든 가격 데이터 분석
  const allPrices: any[] = [];
  const appleByBrand: any[] = [];
  const appleByModelId: any[] = [];
  const iphoneInId: any[] = [];
  const wrongDeviceType: any[] = [];
  
  pricesSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    const id = docSnap.id.toLowerCase();
    const modelId = (data.modelId || '').toLowerCase();
    
    allPrices.push({ id: docSnap.id, ...data });
    
    // brand가 Apple인 경우
    if (data.brand === 'Apple') {
      appleByBrand.push({ id: docSnap.id, ...data });
    }
    
    // modelId에 'apple'이 포함된 경우
    if (modelId.includes('apple')) {
      appleByModelId.push({ id: docSnap.id, ...data });
    }
    
    // id나 modelId에 'iphone'이 포함된 경우
    if (id.includes('iphone') || modelId.includes('iphone')) {
      iphoneInId.push({ id: docSnap.id, ...data });
    }
    
    // deviceType이 smartphone이 아니지만 iPhone인 경우
    if ((id.includes('iphone') || modelId.includes('iphone')) && data.deviceType !== 'smartphone') {
      wrongDeviceType.push({ id: docSnap.id, ...data });
    }
  });
  
  console.log(`\n📊 전체 unified_prices: ${allPrices.length}개`);
  
  console.log(`\n🍎 1. brand='Apple'인 가격: ${appleByBrand.length}개`);
  if (appleByBrand.length > 0) {
    console.log('   deviceType 분포:');
    const byType: Record<string, number> = {};
    appleByBrand.forEach(p => {
      byType[p.deviceType || 'undefined'] = (byType[p.deviceType || 'undefined'] || 0) + 1;
    });
    Object.entries(byType).forEach(([type, count]) => {
      console.log(`     - ${type}: ${count}개`);
    });
  }
  
  console.log(`\n🍎 2. modelId에 'apple' 포함: ${appleByModelId.length}개`);
  if (appleByModelId.length > 0) {
    console.log('   deviceType 분포:');
    const byType: Record<string, number> = {};
    appleByModelId.forEach(p => {
      byType[p.deviceType || 'undefined'] = (byType[p.deviceType || 'undefined'] || 0) + 1;
    });
    Object.entries(byType).forEach(([type, count]) => {
      console.log(`     - ${type}: ${count}개`);
    });
    
    console.log('   brand 분포:');
    const byBrand: Record<string, number> = {};
    appleByModelId.forEach(p => {
      byBrand[p.brand || 'undefined'] = (byBrand[p.brand || 'undefined'] || 0) + 1;
    });
    Object.entries(byBrand).forEach(([brand, count]) => {
      console.log(`     - ${brand}: ${count}개`);
    });
  }
  
  console.log(`\n📱 3. ID/modelId에 'iphone' 포함: ${iphoneInId.length}개`);
  if (iphoneInId.length > 0) {
    console.log('   deviceType 분포:');
    const byType: Record<string, number> = {};
    iphoneInId.forEach(p => {
      byType[p.deviceType || 'undefined'] = (byType[p.deviceType || 'undefined'] || 0) + 1;
    });
    Object.entries(byType).forEach(([type, count]) => {
      console.log(`     - ${type}: ${count}개`);
    });
    
    console.log('   brand 분포:');
    const byBrand: Record<string, number> = {};
    iphoneInId.forEach(p => {
      byBrand[p.brand || 'undefined'] = (byBrand[p.brand || 'undefined'] || 0) + 1;
    });
    Object.entries(byBrand).forEach(([brand, count]) => {
      console.log(`     - ${brand}: ${count}개`);
    });
    
    // 샘플 출력
    console.log('\n   📋 샘플 (10개):');
    iphoneInId.slice(0, 10).forEach(p => {
      const hasPrice = p.gradePrices && (p.gradePrices.A > 0 || p.gradePrices.B > 0);
      console.log(`     ${hasPrice ? '💰' : '⚠️'} ${p.id.substring(0, 60)}...`);
      console.log(`        brand: ${p.brand}, deviceType: ${p.deviceType}`);
      console.log(`        modelId: ${p.modelId?.substring(0, 50)}...`);
      if (hasPrice) {
        console.log(`        A등급: ${p.gradePrices.A}원`);
      }
    });
  }
  
  console.log(`\n⚠️ 4. iPhone인데 deviceType이 smartphone이 아닌 것: ${wrongDeviceType.length}개`);
  if (wrongDeviceType.length > 0) {
    wrongDeviceType.forEach(p => {
      console.log(`   - ${p.id}`);
      console.log(`     deviceType: ${p.deviceType} (should be 'smartphone')`);
      console.log(`     brand: ${p.brand}`);
    });
  }
  
  // 레거시 컬렉션 확인
  console.log('\n🗂️ 5. 레거시 가격 컬렉션 확인...');
  const legacyCollections = [
    'grade_prices_apple_smartphone',
    'prices_apple_smartphone',
    'apple_smartphone_prices'
  ];
  
  for (const collName of legacyCollections) {
    try {
      const snap = await getDocs(collection(db, collName));
      if (snap.size > 0) {
        console.log(`   📦 ${collName}: ${snap.size}개 발견!`);
        snap.docs.slice(0, 3).forEach(doc => {
          console.log(`      - ${doc.id}`);
        });
      }
    } catch (e) {
      // 컬렉션이 없으면 무시
    }
  }
  
  console.log('\n============================================');
  console.log('📌 결론:');
  console.log(`   - iPhone 관련 가격 총: ${iphoneInId.length}개`);
  console.log(`   - brand='Apple' && deviceType='smartphone': ${appleByBrand.filter(p => p.deviceType === 'smartphone').length}개`);
  if (wrongDeviceType.length > 0) {
    console.log(`   ⚠️ deviceType 오류 수정 필요: ${wrongDeviceType.length}개`);
  }
}

/**
 * 🔧 Apple 스마트폰 가격 deviceType/brand 수정
 */
export async function fixAppleSmartphonePrices(): Promise<void> {
  console.log('🔧 Apple 스마트폰 가격 데이터 수정 시작...');
  console.log('============================================');
  
  const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
  
  const toFix: { id: string; data: any; fixes: string[] }[] = [];
  
  pricesSnapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    const id = docSnap.id.toLowerCase();
    const modelId = (data.modelId || '').toLowerCase();
    
    // iPhone 관련 가격인지 확인
    if (id.includes('iphone') || modelId.includes('iphone') || modelId.includes('apple')) {
      const fixes: string[] = [];
      
      // brand 수정 필요
      if (data.brand !== 'Apple') {
        fixes.push(`brand: '${data.brand}' → 'Apple'`);
      }
      
      // deviceType 수정 필요 (tablet이나 wearable로 잘못 설정된 경우)
      if (data.deviceType !== 'smartphone' && modelId.includes('iphone')) {
        fixes.push(`deviceType: '${data.deviceType}' → 'smartphone'`);
      }
      
      if (fixes.length > 0) {
        toFix.push({ id: docSnap.id, data, fixes });
      }
    }
  });
  
  console.log(`\n🔍 수정 필요한 문서: ${toFix.length}개`);
  
  if (toFix.length === 0) {
    console.log('✅ 수정할 문서가 없습니다.');
    return;
  }
  
  // 미리보기
  console.log('\n📋 수정 예정 목록:');
  toFix.slice(0, 20).forEach(item => {
    console.log(`  - ${item.id.substring(0, 50)}...`);
    item.fixes.forEach(fix => console.log(`    ${fix}`));
  });
  if (toFix.length > 20) {
    console.log(`  ... 그 외 ${toFix.length - 20}개 더 있음`);
  }
  
  // 실제 수정
  console.log('\n🔄 수정 실행 중...');
  let fixed = 0;
  
  for (const item of toFix) {
    try {
      const updates: any = {
        updatedAt: Timestamp.now(),
        updatedBy: 'apple-smartphone-fix'
      };
      
      const id = item.id.toLowerCase();
      const modelId = (item.data.modelId || '').toLowerCase();
      
      if (item.data.brand !== 'Apple' && (modelId.includes('apple') || id.includes('iphone'))) {
        updates.brand = 'Apple';
      }
      
      if (item.data.deviceType !== 'smartphone' && modelId.includes('iphone')) {
        updates.deviceType = 'smartphone';
      }
      
      await setDoc(doc(db, 'unified_prices', item.id), {
        ...item.data,
        ...updates
      });
      
      fixed++;
      if (fixed % 10 === 0) {
        console.log(`  ${fixed}/${toFix.length} 완료...`);
      }
    } catch (error) {
      console.error(`  ❌ 실패: ${item.id}`, error);
    }
  }
  
  console.log('\n============================================');
  console.log(`✅ 수정 완료: ${fixed}/${toFix.length}개`);
  console.log('\n📌 다음 단계:');
  console.log('   await window.__diagnoseApplePricesDeep()  // 결과 확인');
}

// ============================================
// 🗑️ 레거시 가격 정리 함수들
// ============================================

/**
 * 레거시 가격 정리 미리보기
 * [배경] 마이그레이션 후 남은 legacy-samsung-samsung_스마트폰_... 형식의 고아 가격 확인
 */
const previewLegacyPriceCleanup = async (): Promise<void> => {
  console.log('🔍 레거시 가격 정리 미리보기');
  console.log('============================================');
  console.log('⚠️ 이 작업은 데이터를 변경하지 않습니다.');
  
  // 1. unified_prices에서 레거시 가격 찾기
  const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
  
  const legacyPrices: Array<{
    id: string;
    modelId: string;
    brand: string;
    deviceType: string;
    gradePrices: any;
  }> = [];
  
  pricesSnapshot.forEach(doc => {
    const data = doc.data();
    const id = doc.id;
    const modelId = data.modelId || '';
    
    // 레거시 패턴 확인
    // 1. legacy- 접두어
    // 2. 언더스코어(_) 기반 ID
    // 3. 한글 포함 ID
    const isLegacy = 
      id.startsWith('legacy-') ||
      modelId.startsWith('legacy-') ||
      id.includes('_스마트폰_') ||
      modelId.includes('_스마트폰_') ||
      id.includes('_태블릿_') ||
      modelId.includes('_태블릿_') ||
      id.includes('_웨어러블_') ||
      modelId.includes('_웨어러블_');
    
    if (isLegacy) {
      legacyPrices.push({
        id,
        modelId,
        brand: data.brand || 'unknown',
        deviceType: data.deviceType || 'unknown',
        gradePrices: data.gradePrices || {}
      });
    }
  });
  
  // 2. 결과 출력
  console.log(`\n🗑️ 삭제 예정 레거시 가격: ${legacyPrices.length}개`);
  
  if (legacyPrices.length === 0) {
    console.log('✅ 삭제할 레거시 가격이 없습니다.');
    return;
  }
  
  // 카테고리별 분류
  const byCategory: Record<string, typeof legacyPrices> = {};
  legacyPrices.forEach(p => {
    const cat = p.deviceType || 'unknown';
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(p);
  });
  
  console.log('\n📊 카테고리별 분류:');
  Object.entries(byCategory).forEach(([cat, items]) => {
    console.log(`  ${cat}: ${items.length}개`);
  });
  
  console.log('\n📋 삭제 예정 목록 (20개 샘플):');
  legacyPrices.slice(0, 20).forEach(p => {
    const priceInfo = p.gradePrices?.A ? `A: ${p.gradePrices.A}원` : '가격없음';
    console.log(`  - ${p.id.substring(0, 60)}...`);
    console.log(`    modelId: ${p.modelId.substring(0, 50)}...`);
    console.log(`    brand: ${p.brand}, deviceType: ${p.deviceType}, ${priceInfo}`);
  });
  
  if (legacyPrices.length > 20) {
    console.log(`  ... 그 외 ${legacyPrices.length - 20}개 더 있음`);
  }
  
  console.log('\n============================================');
  console.log('📌 실제 삭제를 실행하려면:');
  console.log('   await window.__cleanupLegacyPrices()');
};

/**
 * 레거시 가격 실제 삭제
 * [주의] 삭제된 데이터는 복구할 수 없습니다!
 */
const cleanupLegacyPrices = async (): Promise<void> => {
  console.log('🗑️ 레거시 가격 정리 시작');
  console.log('============================================');
  
  // 1. unified_prices에서 레거시 가격 찾기
  const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
  
  const legacyPriceIds: string[] = [];
  
  pricesSnapshot.forEach(doc => {
    const data = doc.data();
    const id = doc.id;
    const modelId = data.modelId || '';
    
    // 레거시 패턴 확인
    const isLegacy = 
      id.startsWith('legacy-') ||
      modelId.startsWith('legacy-') ||
      id.includes('_스마트폰_') ||
      modelId.includes('_스마트폰_') ||
      id.includes('_태블릿_') ||
      modelId.includes('_태블릿_') ||
      id.includes('_웨어러블_') ||
      modelId.includes('_웨어러블_');
    
    if (isLegacy) {
      legacyPriceIds.push(id);
    }
  });
  
  if (legacyPriceIds.length === 0) {
    console.log('✅ 삭제할 레거시 가격이 없습니다.');
    return;
  }
  
  console.log(`🗑️ ${legacyPriceIds.length}개 레거시 가격 삭제 시작...`);
  
  // 2. 배치로 삭제 (500개씩)
  let deleted = 0;
  const batchSize = 500;
  
  for (let i = 0; i < legacyPriceIds.length; i += batchSize) {
    const batch = writeBatch(db);
    const chunk = legacyPriceIds.slice(i, i + batchSize);
    
    chunk.forEach(id => {
      batch.delete(doc(db, 'unified_prices', id));
    });
    
    await batch.commit();
    deleted += chunk.length;
    console.log(`  ${deleted}/${legacyPriceIds.length} 삭제 완료...`);
  }
  
  console.log('\n============================================');
  console.log(`✅ 레거시 가격 삭제 완료: ${deleted}개`);
  console.log('\n📌 다음 단계:');
  console.log('   await window.__diagnoseSmartphonePrices("Samsung")  // 결과 확인');
};

/**
 * 고아 가격 정리 (device_catalog에 모델이 없는 가격)
 * [배경] 마이그레이션 후 모델이 병합되어 더 이상 사용되지 않는 가격 정리
 */
const previewOrphanPriceCleanup = async (deviceType: 'smartphone' | 'tablet' | 'wearable'): Promise<void> => {
  console.log(`🔍 ${deviceType} 고아 가격 정리 미리보기`);
  console.log('============================================');
  
  // 1. device_catalog에서 해당 deviceType 모델 ID 수집
  const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
  const validModelIds = new Set<string>();
  
  catalogSnapshot.forEach(doc => {
    const data = doc.data();
    if (data.deviceType === deviceType) {
      validModelIds.add(doc.id);
    }
  });
  
  console.log(`📦 ${deviceType} 모델: ${validModelIds.size}개`);
  
  // 2. unified_prices에서 해당 deviceType 가격 확인
  const pricesSnapshot = await getDocs(
    query(collection(db, 'unified_prices'), where('deviceType', '==', deviceType))
  );
  
  const orphanPrices: Array<{
    id: string;
    modelId: string;
    gradePrices: any;
  }> = [];
  
  pricesSnapshot.forEach(doc => {
    const data = doc.data();
    const modelId = data.modelId || '';
    
    // 레거시 가격은 별도 함수로 처리
    if (modelId.startsWith('legacy-') || modelId.includes('_')) {
      return;
    }
    
    // device_catalog에 모델이 없으면 고아
    if (!validModelIds.has(modelId)) {
      orphanPrices.push({
        id: doc.id,
        modelId,
        gradePrices: data.gradePrices || {}
      });
    }
  });
  
  console.log(`🔗 고아 가격: ${orphanPrices.length}개`);
  
  if (orphanPrices.length === 0) {
    console.log('✅ 고아 가격이 없습니다.');
    return;
  }
  
  console.log('\n📋 고아 가격 목록:');
  orphanPrices.forEach(p => {
    const priceInfo = p.gradePrices?.A ? `A: ${p.gradePrices.A}원` : '가격없음';
    console.log(`  - ${p.id}`);
    console.log(`    modelId: ${p.modelId} (device_catalog에 없음)`);
    console.log(`    ${priceInfo}`);
  });
  
  console.log('\n============================================');
  console.log('⚠️ 고아 가격은 수동 확인 후 삭제하세요.');
  console.log('   일부는 device_catalog에 모델 추가가 필요할 수 있습니다.');
};

// 🔌 window 객체에 함수 등록
if (typeof window !== 'undefined') {
  (window as any).__scanSmartphoneData = scanSmartphoneData;
  (window as any).__diagnoseSmartphonePrices = diagnoseSmartphonePrices;
  (window as any).__previewSmartphoneMigration = previewSmartphoneMigration;
  (window as any).__migrateSmartphones = migrateSmartphones;
  (window as any).__analyzeSmartphoneMergeConflicts = analyzeSmartphoneMergeConflicts;
  (window as any).__diagnoseApplePricesDeep = diagnoseApplePricesDeep;
  (window as any).__fixAppleSmartphonePrices = fixAppleSmartphonePrices;
  (window as any).__previewLegacyPriceCleanup = previewLegacyPriceCleanup;
  (window as any).__cleanupLegacyPrices = cleanupLegacyPrices;
  (window as any).__previewOrphanPriceCleanup = previewOrphanPriceCleanup;
  
  console.log('📱 스마트폰 마이그레이션 함수 등록 완료:');
  console.log('  🔍 진단:');
  console.log('    - window.__scanSmartphoneData(): 스마트폰 데이터 스캔');
  console.log('    - window.__diagnoseSmartphonePrices("Apple" | "Samsung"): 브랜드별 가격 진단');
  console.log('    - window.__diagnoseApplePricesDeep(): Apple 가격 심층 진단');
  console.log('    - window.__analyzeSmartphoneMergeConflicts(): 중복 병합 분석');
  console.log('  🔧 수정:');
  console.log('    - window.__fixAppleSmartphonePrices(): Apple 가격 brand/deviceType 수정');
  console.log('  🗑️ 정리:');
  console.log('    - window.__previewLegacyPriceCleanup(): 레거시 가격 삭제 미리보기');
  console.log('    - window.__cleanupLegacyPrices(): 레거시 가격 실제 삭제');
  console.log('    - window.__previewOrphanPriceCleanup("smartphone"): 고아 가격 확인');
  console.log('  🔄 마이그레이션:');
  console.log('    - window.__previewSmartphoneMigration(): 미리보기');
  console.log('    - window.__migrateSmartphones(): 실제 실행');
}

